<script setup>
import { computed, inject, onMounted, useTemplateRef, ref } from 'vue';
import { useCustomerStore } from './store/customer';
import { loadExternalScript, isNumeric, formatMoneyValue, statesOptions } from './functions';
import VueSelect from "vue3-select-component";

import SvgNote from '@svg/note.svg?raw';

const customerStore = useCustomerStore();
const appConfig = inject('appConfig');
const merchant = appConfig?.merchant;

// for loader animation
const collectWrapper = useTemplateRef('collectWrapper');

// for dynamic loaded frames
const ccn = useTemplateRef('ccnField');
const cce = useTemplateRef('cceField');
const ccv = useTemplateRef('ccvField');

// for submitting animation, mainly
const submitBtn = useTemplateRef('submit-btn');

const errorsContainer = useTemplateRef('errors-container');
const successContainer = useTemplateRef('success-container');

let btnText = computed(() => {
  if (isNumeric(customerStore.amount) && customerStore.amount > 0) {
    let val = formatMoneyValue(customerStore.amount);
    return `Pay ${val}`;
  } else {
    return 'Amount is empty';
  }
});


let handleTokenRequest = () => {
  errorsContainer.value.textContent = '';
  successContainer.value.textContent = '';
  if ( window['CollectJS'] ) {
      if ( customerStore.meta.valid && customerStore.amount ) {
        submitBtn.value.setAttribute('data-processing', 'data-processing');
        submitBtn.value.setAttribute('disabled', 'disabled');
        CollectJS.startPaymentRequest();
      }
    } else {
      console.error('CollectJS object is not found (tokenize flow)');
    }
}

const apiClient = inject('apiClient');
let handleSumbit = (tokenRequestResult) => {
  console.log('Payment token: %o', tokenRequestResult);
  errorsContainer.value.textContent = '';
  successContainer.value.textContent = '';

  const response = apiClient
    .post('pay' , {
      firstname:  customerStore.firstName,
      lastname:   customerStore.lastName,
      email:      customerStore.email,
      amount:     customerStore.amount,
      address1:   customerStore.address,
      city:       customerStore.city,
      state:      customerStore.state,
      zip:        customerStore.zip,
      merchant_id: merchant?.id,
      payment_token: tokenRequestResult.token,
      message: ''             // honeypot ^^
    })
    .then(response => {
      if (response.status >= 200 && response.status < 300) {
        if (response.data.state == 'success') {
          errorsContainer.value.textContent = '';
          successContainer.value.textContent = response.data.message;

          // Clear payment inputs after success payment
          if ( window['CollectJS'] ) {
            CollectJS.clearInputs();
          }
          customerStore.setFieldValue('amount', undefined);
          customerStore.setFieldValue('ccnumber', '1');
          customerStore.setFieldValue('ccexp', '1');
          customerStore.validate();
        } else {
          errorsContainer.value.textContent = response.data.message;
          successContainer.value.textContent = '';
        }
      } else {
        return Promise.resolve(response);
      }
    })
    .catch(response => {
      successContainer.value.textContent = '';
      console.log(response);
      errorsContainer.value.textContent = response?.response?.data?.message || response?.data?.message || 'Unexpected error. Contact system administrator.'
      console.log(response);
    })
    .finally(() => {
      submitBtn.value.removeAttribute('data-processing');
      submitBtn.value.removeAttribute('disabled');
    })
} 

const paymentConfig = {
  // paymentSelector: "#payment-btn",
  fields: {
    ccnumber: {
      selector: "#ccnumber",
      title: "Card Number",
      placeholder: "0000 0000 0000 0000"
    },
    ccexp: {
      selector: "#ccexp",
      title: "Card Expiration",
      placeholder: "00 / 00"
    },
    cvv: {
      display : "show",
      selector  : "#cvv",
      title : "CVV Code",
      placeholder : "***"
    },
  },
  variant: "inline",
  googleFont: "Poppins:500",
  currency: "USD",  // static
  country: "US",    // static
  timeoutDuration: 30 * 1000,
  timeoutCallback: ()=>{
    console.log("The tokenization didn't respond in the expected timeframe. This could be due to an invalid or incomplete field or poor connectivity");
  },
  fieldsAvailableCallback: () => {
    // remove loader attribute from NMI fields wrapper
    collectWrapper.value.removeAttribute('data-loading');

    // console.log("Collect.js loaded the fields onto the form");
    customerStore.setFieldError('ccnumber', 'Fields empty');
    customerStore.setFieldError('ccexp', 'Fields empty');
  },
  validationCallback: (field, status, message) => {
    switch (field) {
      case 'ccnumber':
        if (status) {
          customerStore.ccnumber = '';
          customerStore.setFieldError('ccnumber', undefined);
          ccn.value.classList.remove('text-field--error');
        } else {
          customerStore.setFieldError('ccnumber', message);
          cce.value.classList.add('text-field--error');
        }
        break;
      case 'ccexp':
        if (status) {
          customerStore.ccexp = '';
          customerStore.setFieldError('ccexp', undefined);
          ccv.value.classList.remove('text-field--error');
        } else {
          customerStore.setFieldError('ccexp', message);
          ccv.value.classList.add('text-field--error');
        }
        break;
      case 'cvv':
        if (status) {
          customerStore.setFieldError('cvv', undefined);
        } else {
          customerStore.setFieldError('cvv', message);
        }
        break;
      default:
        break;
    }
  },
  callback: (response) => {
    handleSumbit(response);
  },
}
onMounted(() => {
  // set loader attribute on NMI fields wrapper
  collectWrapper.value.setAttribute('data-loading', true)

  loadExternalScript('https://secure.nmi.com/token/Collect.js', { 'data-tokenization-key': appConfig?.merchant?.settings?.public_key })
    .then((data) => { // DOMScriptElement here
      if ( window['CollectJS'] ) {
        CollectJS.configure(paymentConfig);
      } else {
        console.error('CollectJS object is not found (config)');
      }
  })
  .catch((data) => {
    console.log('Collect.js script fetch failed', data);
  });
});

</script>

<template>
  <div class="checkout">

    <div class="checkout__header" v-if="merchant?.logo || merchant?.name">
      <img v-if="merchant?.logo" :src="merchant?.logo" class="merchant-logo"/>
      <div v-if="merchant?.name" v-text="merchant?.name" class="merchant-name"></div>
    </div>

    <div class="checkout__body">
      <div class="form-row form-row--mobile">
        <div class="text-field">
          <input name="firstName" type="text" placeholder="" v-model="customerStore.firstName" v-bind="customerStore.firstNameProps" />
          <label class="fieldLabel" for="firstName">First Name</label>
          <span v-show="customerStore.errors.firstName" class="field-error" role="alert">
            <div class="icon" v-html="SvgNote"></div>
            {{ customerStore.errors.firstName }}
          </span>
        </div>
        <div class="text-field">
          <input name="lastName" type="text" placeholder="" v-model="customerStore.lastName" v-bind="customerStore.lastNameProps" />
          <label class="fieldLabel" for="lastName">Last Name</label>
          <span v-show="customerStore.errors.lastName" class="field-error" role="alert">
            <div class="icon" v-html="SvgNote"></div>
            {{ customerStore.errors.lastName }}
          </span>
        </div>
      </div>

      <div class="text-field" :class="{ 'text-field--error': customerStore.errors.email }">
        <input name="email" type="text" placeholder="" v-model="customerStore.email" v-bind="customerStore.emailProps" />
        <label class="fieldLabel" for="email">Email</label>
        <span v-show="customerStore.errors.email" class="field-error" role="alert">
          <div class="icon" v-html="SvgNote"></div>
          {{ customerStore.errors.email }}
        </span>
      </div>

      <div class="text-field">
        <input name="address" type="text" placeholder="" v-model="customerStore.address" v-bind="customerStore.addressProps" />
        <label class="fieldLabel" for="address">Address</label>
        <span v-show="customerStore.errors.address" class="field-error" role="alert">
          <div class="icon" v-html="SvgNote"></div>
          {{ customerStore.errors.address }}
        </span>
      </div>

      <div class="form-row form-row--mobile">
        <div class="text-field">
          <input name="city" type="text" placeholder="" v-model="customerStore.city" v-bind="customerStore.cityProps" />
          <label class="fieldLabel" for="city">City</label>
          <span v-show="customerStore.errors.city" class="field-error" role="alert">
            <div class="icon" v-html="SvgNote"></div>
            {{ customerStore.errors.city }}
          </span>
        </div>
        
        <div v-if="false" class="text-field">
          <!-- <input name="state" type="text" placeholder="" v-model="customerStore.state" v-bind="customerStore.stateProps" />
          <label class="fieldLabel" for="address">State</label>
          <span v-show="customerStore.errors.state" class="field-error" role="alert">
            <div class="icon" v-html="SvgNote"></div>
            {{ customerStore.errors.state }}
          </span> -->
        </div>

        <div class="select-field">
          <VueSelect v-model="customerStore.state" :options="statesOptions" placeholder="State" :is-searchable="false" :is-clearable="true" />
          <span v-show="customerStore.errors.state" class="field-error" role="alert">
            <div class="icon" v-html="SvgNote"></div>
            {{ customerStore.errors.state }}
          </span>
        </div>

      </div>


      <div class="text-field">
        <input name="zip" type="text" placeholder="" v-model="customerStore.zip" v-bind="customerStore.zipProps" />
        <label class="fieldLabel" for="zip">Postal code</label>
        <span v-show="customerStore.errors.zip" class="field-error" role="alert">
          <div class="icon" v-html="SvgNote"></div>
          {{ customerStore.errors.zip }}
        </span>
      </div>

      <div class="text-field">
        <input name="amount" type="text" placeholder="" v-model="customerStore.amount"
          v-bind="customerStore.amountProps" />
        <label class="fieldLabel" for="amount">Amount</label>
        <span v-show="customerStore.errors.amount" class="field-error" role="alert">
          <div class="icon" v-html="SvgNote"></div>
          {{ customerStore.errors.amount }}
        </span>
      </div>

    </div>

    <!-- Collect.js -->
    <div class="checkout__collectjs" ref="collectWrapper" data-loading>
      <div class="text-field text-field--frame" ref="ccnField">
        <div id="ccnumber"></div>
        <label class="fieldLabel" for="ccnumber">Card Number</label>
        <span v-show="customerStore.errors.ccnumber" class="field-error" role="alert">
          <div class="icon" v-html="SvgNote"></div>
          {{ customerStore.errors.ccnumber }}
        </span>
      </div>
      <div class="form-row">
        <div class="text-field text-field--frame" ref="cceField">
          <div id="ccexp"></div>
          <label class="fieldLabel" for="ccexp">Exp. date</label>
          <span v-show="customerStore.errors.ccexp" class="field-error" role="alert">
            <div class="icon" v-html="SvgNote"></div>
            {{ customerStore.errors.ccexp }}
          </span>
        </div>
        <div class="text-field text-field--frame" ref="ccvField">
          <div id="cvv"></div>
          <label class="fieldLabel" for="cvv">CVV</label>
          <span v-show="customerStore.errors.cvv" class="field-error" role="alert">
            <div class="icon" v-html="SvgNote"></div>
            {{ customerStore.errors.cvv }}
          </span>
        </div>
      </div>
    </div>

    <button type="button" id="payment-btn" class="submit-btn" ref="submit-btn" @click="handleTokenRequest" :disabled="!customerStore.meta.valid || !customerStore.amount || customerStore.amount <= 0">
      {{ btnText }}
    </button>

    <div class="message-container errors-container" ref="errors-container"></div>
    <div class="message-container success-container" ref="success-container"></div>

  </div>
</template>

<style lang="scss">

.message-container {
  box-sizing: border-box;
  width: 100%;
  border-radius: 0.25rem;
  max-height: 912px;
  border-width: 2px;
  border-style: solid;
  transition: max-height 0.2s 0s linear;
  padding: 1rem 1.25rem;
  margin-left: auto;
  margin-right: auto;
  margin-top: 1.75rem;

  &.errors-container {
    border-color: rgba(var(--error-color-rgb), 0.95);
    background-color: rgba(var(--error-color-rgb), 0.02);
  }
  &.success-container {
    border-color: rgba(var(--success-color-rgb), 0.95);
    background-color: rgba(var(--success-color-rgb), 0.03);
  }

  &:empty {
    overflow: hidden;
    padding: 0;
    max-height: 0;
    border-color: transparent !important;
    border-width: 0 !important;
    margin-top: 0 !important;
  }
}

#payment-btn {
  height: 56px;
  width: 100%;
  font-weight: 600;
}

.checkout {
  width: 100%;
  max-width: 640px;
  padding-left: 1rem;
  padding-right: 1rem;
  margin: 1rem auto 1.5rem;
  
  @media (min-width: 576px) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    margin: 1.5rem auto;
  }

  &__header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-weight: 500;

    &:not(:empty) {
      margin-bottom: 1.875rem;
    }

    img {
      height: 60px;
      margin-right: 1.5rem;
    }
  }

  &__body {}

  &__collectjs {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    max-height: 1000px;
    position: relative;
    transition: max-height 0.15s 0s linear;
    margin-bottom: 1.875rem;
    overflow: hidden;

    &::before,
    &::after {
      content: '';
      position: absolute;
      display: block;
      text-align: center;
      z-index: -1;
      opacity: 0;
      transform-origin: 50% 50%;
    }
    
    &::after {          
        left: 50%;
        top: 50%;
        transform: translate3d(-50%, -50%, 0);
      }

    &::before {
        background-color: var(--bg-color);
        width: 100%;
        height: 100%;
        left: 0;
        bottom: 0;
    }

    &::after {
        animation: spinner__animation 1s linear infinite;
        backface-visibility: hidden;
        border: .2em solid var(--form-spinner-color);
        border-left: .2em solid transparent;
        border-radius: 50%;
        margin: -.5em 0 0 -.5em;
        transform: translateZ(0) scale(.5);
        width: 1em;
        height: 1em;
    }
    &[data-loading] {
      max-height: 36px;
      transition: max-height 0.15s 0s linear;

      &::before,
      &::after {
        opacity: 1;
        transition: opacity 0.2s 0s linear;
      }
      &::before {
        z-index: 99;
      }
      &::after {
          z-index: 100;
      } 
    }
  }
}

.form-row {
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  flex-wrap: nowrap;

  .text-field,
  .select-field {
    flex: 0 0 calc(50% - 0.75rem);
  }

  &--mobile {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    .text-field,
    .select-field {
      flex: 0 0 100%;
    }

    @media (min-width: 576px) {
      justify-content: space-between;
      flex-direction: row;
      flex-wrap: nowrap;

      .text-field,
      .select-field {
        flex: 0 0 calc(50% - 0.75rem);
      }
    }
  }
}

.text-field {
  position: relative;
  width: 100%;
  flex: 1 1 100%;
  margin-bottom: 1.875rem;

  label {
    color: var(--input-color);
    line-height: 1;
    font-size: 16px;
    left: 1px;
    padding: 0;
    position: absolute;
    top: 1px;
    transition: font-size .2s ease, margin .15s ease-in-out;
    user-select: none;
    pointer-events: none;
    // margin: 17px 8px 8px;
    margin: 22px 16px 8px;
  }

  input {
    width: 100%;
    position: relative;
    box-shadow: none;
    overflow-y: visible;
    font-size: 1rem;
    line-height: 20px;
    border-radius: 4px;
    margin-bottom: 0;
    margin-top: 0;
    width: inherit;
    background: #fff;
    border: 2px solid var(--border-color);
    outline: none;
    padding: 26px 0.5rem 6px;
    color: var(--input-color);
    height: 56px; // workaround for firefox bug: https://hg.mozilla.org/mozilla-central/rev/b97aef275b5e

    &:hover {
      border-color: var(--input-color);

      &+label {
        color: var(--input-color);
      }
    }

    &:not(:focus)::placeholder,
    &:not(:active)::placeholder {
      color: transparent;
    }

    &:focus,
    &:active {

      &:not([disabled]):not(.text-field-error) {
        border-color: #333;
        outline: 3px solid rgba(var(--success-color-rgb), 0.3);
        background-color: rgba(var(--success-color-rgb), 0.1);
        color: #333;

        &:not([readonly])~label {
          margin-top: 0.5rem;
          margin-left: 0.5rem;
          font-size: 0.75rem;
        }
      }

    }

    &:not(:placeholder-shown):not([value=""])~label {
      margin-top: 0.5rem;
      margin-left: 0.5rem;
      font-size: 0.75rem;
    }

    &::-webkit-input-placeholder,
    &::-moz-placeholder {
      color: (0, 0, 0, 0.5);
      font-size: 0.875rem;
    }
  }

  &--frame>label {
    margin-top: 0.5rem;
    margin-left: 0.5rem;
    font-size: 0.75rem;
  }

  &--error {
    input {
      border-color: rgba(var(--error-color-rgb), 0.67);
      background-color: rgba(var(--error-color-rgb), 0.1);
    }
  }

  .field-error {
    // font-size: 0.75rem;
    font-size: 0.625rem;
    color: var(--error-color);
    line-height: 0.75rem;
    top: 100%;
    left: 0;
    position: absolute;
    letter-spacing: -0.25px;

    &:not(:empty) {
      // margin-top: 5px;
      margin-top: 2px;
    }

    .icon {
      height: 0.75rem;
      width: 0.75rem;
      margin-right: 0.125rem;
      margin-top: -0.25rem;
      display: inline-block;
      svg {
        vertical-align: middle;
      }
    }
  }
}

.select-field {
  position: relative;
  width: 100%;
  flex: 1 1 100%;
  margin-bottom: 1.875rem;

  .vue-select {

    // vue-select field settings
    --vs-border: 2px solid var(--border-color);
    --vs-text-color: var(--input-color);
    --vs-option-text-color: var(--input-color);
    --vs-input-outline: var(--border-color);
    --vs-option-focused-color: rgba(var(--success-color-rgb), 0.005);
    --vs-option-hover-color: rgba(var(--success-color-rgb), 0.2)
    --vs-option-selected-color: rgba(var(--success-color-rgb), 0.3);
    --vs-padding: 0.25rem 0.5rem 0.25rem 1rem;
    --vs-input-placeholder-color: var(--input-color);

    .menu-option.selected {
      background-color: rgba(var(--success-color-rgb), 0.3);
    }
    .control {
      height: 56px;
  
      &.focused {
        box-shadow: none !important;
        border-color: #5a5a5a;
        outline: 3px solid rgba(var(--success-color-rgb), 0.3);
        background-color: rgba(var(--success-color-rgb), 0.1)
      }
    }
  
    .clear-button,
    .dropdown-icon {
      display: inline-flex;
      align-content: center;
      align-items: center;
      justify-content: center;
    }

    .search-input {
      &::placeholder {
        opacity: 1;
      }
    }
  }
  .field-error {
    // font-size: 0.75rem;
    font-size: 0.625rem;
    color: var(--error-color);
    line-height: 0.75rem;
    top: 100%;
    left: 0;
    position: absolute;
    letter-spacing: -0.25px;

    &:not(:empty) {
      // margin-top: 5px;
      margin-top: 2px;
    }

    .icon {
      height: 0.75rem;
      width: 0.75rem;
      margin-right: 0.125rem;
      margin-top: -0.25rem;
      display: inline-block;
      svg {
        vertical-align: middle;
      }
    }
  }
}
</style>